/**
 * Arithmétique exacte sur des décimaux en texte (R2, ADR-133 §4) : totaux d'un onglet `lines` et
 * seuil saisi par l'utilisateur. Rien ne passe par `number` : la valeur est mise à l'échelle en
 * entier (`bigint`) puis rendue en texte, sans zéro superflu.
 */

const DECIMAL = /^-?\d+(\.\d+)?$/;

export function isDecimalText(value: unknown): value is string {
  return typeof value === 'string' && DECIMAL.test(value);
}

/** Saisie de l'utilisateur (« 1,5 », « 0.25 ») en décimal texte, ou `null` si elle n'en est pas un. */
export function parseDecimalInput(input: string): string | null {
  const normalized = input.trim().replace(/\s/g, '').replace(',', '.');
  return DECIMAL.test(normalized) ? normalized.replace(/^(-?)0+(?=\d)/, '$1') : null;
}

function scaled(value: string, scale: number): bigint {
  const negative = value.startsWith('-');
  const [whole, fraction = ''] = (negative ? value.slice(1) : value).split('.');
  const digits = BigInt(`${whole}${fraction.padEnd(scale, '0')}`);
  return negative ? -digits : digits;
}

const scaleOf = (value: string): number => value.split('.')[1]?.length ?? 0;

function text(value: bigint, scale: number): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(scale + 1, '0');
  const whole = scale === 0 ? digits : digits.slice(0, -scale);
  const fraction = scale === 0 ? '' : digits.slice(-scale).replace(/0+$/, '');
  const body = fraction === '' ? whole : `${whole}.${fraction}`;
  return negative && body !== '0' ? `-${body}` : body;
}

/** Somme exacte de décimaux en texte. */
export function sumDecimalText(values: readonly string[]): string {
  const scale = Math.max(0, ...values.map(scaleOf));
  return text(values.reduce((total, value) => total + scaled(value, scale), 0n), scale);
}

/** Comparaison exacte des valeurs absolues : négatif si |a| < |b|, zéro si égales, positif sinon. */
export function compareAbsDecimalText(a: string, b: string): number {
  const scale = Math.max(scaleOf(a), scaleOf(b));
  const left = scaled(a.replace(/^-/, ''), scale);
  const right = scaled(b.replace(/^-/, ''), scale);
  return left === right ? 0 : left < right ? -1 : 1;
}

export function isZeroDecimalText(value: string): boolean {
  return /^-?0+(\.0+)?$/.test(value);
}
